import * as React from "react";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

/**
 * MobileSheet — uses Vaul Drawer (bottom sheet) on mobile
 * and Radix Dialog on desktop. Provides a single API for both.
 *
 * Usage:
 *   <MobileSheet open={open} onOpenChange={setOpen}>
 *     <MobileSheetContent>
 *       <MobileSheetHeader>
 *         <MobileSheetTitle>Title</MobileSheetTitle>
 *         <MobileSheetDescription>Desc</MobileSheetDescription>
 *       </MobileSheetHeader>
 *       ...content
 *     </MobileSheetContent>
 *   </MobileSheet>
 */

type RootProps = {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode;
};

const MobileSheetCtx = React.createContext<{ isMobile: boolean }>({ isMobile: false });

export function MobileSheet({ open, onOpenChange, children }: RootProps) {
  const isMobile = useIsMobile();
  return (
    <MobileSheetCtx.Provider value={{ isMobile }}>
      {isMobile ? (
        <Drawer open={open} onOpenChange={onOpenChange}>
          {children}
        </Drawer>
      ) : (
        <Dialog open={open} onOpenChange={onOpenChange}>
          {children}
        </Dialog>
      )}
    </MobileSheetCtx.Provider>
  );
}

export function MobileSheetTrigger({ children, asChild }: { children: React.ReactNode; asChild?: boolean }) {
  const { isMobile } = React.useContext(MobileSheetCtx);
  return isMobile ? (
    <DrawerTrigger asChild={asChild}>{children}</DrawerTrigger>
  ) : (
    <DialogTrigger asChild={asChild}>{children}</DialogTrigger>
  );
}

export function MobileSheetContent({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const { isMobile } = React.useContext(MobileSheetCtx);
  return isMobile ? (
    <DrawerContent className={cn("max-h-[92vh]", className)}>
      <div className="overflow-y-auto px-4 pb-6">{children}</div>
    </DrawerContent>
  ) : (
    <DialogContent className={cn("w-[calc(100vw-2rem)] max-w-lg", className)}>
      {children}
    </DialogContent>
  );
}

export function MobileSheetHeader({ children, className }: { children: React.ReactNode; className?: string }) {
  const { isMobile } = React.useContext(MobileSheetCtx);
  return isMobile ? (
    <DrawerHeader className={className}>{children}</DrawerHeader>
  ) : (
    <DialogHeader className={className}>{children}</DialogHeader>
  );
}

export function MobileSheetTitle({ children, className }: { children: React.ReactNode; className?: string }) {
  const { isMobile } = React.useContext(MobileSheetCtx);
  return isMobile ? (
    <DrawerTitle className={className}>{children}</DrawerTitle>
  ) : (
    <DialogTitle className={className}>{children}</DialogTitle>
  );
}

export function MobileSheetDescription({ children, className }: { children: React.ReactNode; className?: string }) {
  const { isMobile } = React.useContext(MobileSheetCtx);
  return isMobile ? (
    <DrawerDescription className={className}>{children}</DrawerDescription>
  ) : (
    <DialogDescription className={className}>{children}</DialogDescription>
  );
}
